/** @file expandToRgba: turns unfiltered scanlines of any supported colour type into an RGBA8 image. */
import { throwError } from '@zilo/pose3d-std/errors/throwError';
import { IMAGE_IO_ERRORS } from '../errors/imageIoErrors';
import { createImage } from '../image/createImage';
import type { Image } from '../types/image';
import { channelsOfColorType } from './channelsOfColorType';
import type { PngHeader } from './pngHeader';

/** Sample `i` of the row starting at `row`, at its full precision (0..2^d − 1). */
function sampleAt(rows: Uint8Array, row: number, i: number, d: number): number {
  if (d === 16) return ((rows[row + i * 2] ?? 0) << 8) | (rows[row + i * 2 + 1] ?? 0);
  if (d === 8) return rows[row + i] ?? 0;
  const byte = rows[row + ((i * d) >> 3)] ?? 0;
  return (byte >> (8 - d - ((i * d) & 7))) & ((1 << d) - 1);
}

/** The tRNS colour key of a grey (1 sample) or RGB (3 samples) image, or null. */
function colourKey(colorType: number, trns: Uint8Array | undefined): number[] | null {
  if (trns === undefined || (colorType !== 0 && colorType !== 2)) return null;
  const n = colorType === 0 ? 1 : 3;
  if (trns.length < n * 2) return null;
  return Array.from({ length: n }, (_, c) => ((trns[c * 2] ?? 0) << 8) | (trns[c * 2 + 1] ?? 0));
}

type Ctx = { header: PngHeader; palette: Uint8Array | undefined; trns: Uint8Array | undefined; key: number[] | null };

/** The RGBA of one pixel from its samples scaled to 8 bits (`e`) and at full precision (`s`). */
type Expand = (e: number[], s: number[], keyed: boolean, ctx: Ctx) => readonly number[];
/** One expansion per colour type. */
const EXPAND: Record<PngHeader['colorType'], Expand> = {
  0: ([g = 0], _s, keyed) => [g, g, g, keyed ? 0 : 255],
  2: ([r = 0, g = 0, b = 0], _s, keyed) => [r, g, b, keyed ? 0 : 255],
  4: ([g = 0, a = 0]) => [g, g, g, a],
  6: (e) => e,
  3: (_e, [i = 0], _keyed, ctx) => {
    const p = ctx.palette;
    if (p === undefined || i * 3 + 2 >= p.length) throwError(IMAGE_IO_ERRORS.PNG_COLOR_TYPE, { index: i }, { path: '/png/PLTE' });
    return [p[i * 3] ?? 0, p[i * 3 + 1] ?? 0, p[i * 3 + 2] ?? 0, ctx.trns?.[i] ?? 255];
  },
};

/** Writes the RGBA of one pixel whose full-precision samples are `s` into `out` at `o`. */
function writePixel(out: Uint8ClampedArray, o: number, s: number[], ctx: Ctx): void {
  const d = ctx.header.bitDepth;
  const e = d === 16 ? s.map((v) => v >> 8) : d === 8 ? s : s.map((v) => (v * 255) / ((1 << d) - 1));
  const keyed = ctx.key !== null && ctx.key.every((k, c) => s[c] === k);
  out.set(EXPAND[ctx.header.colorType](e, s, keyed, ctx), o);
}

/** RGBA8 of unfiltered `rows`; 16-bit keeps the high byte, low depths scale to 0..255, tRNS applies to types 0, 2 and 3. */
export function expandToRgba(rows: Uint8Array, header: PngHeader, palette?: Uint8Array, trns?: Uint8Array): Image {
  const { width, height, bitDepth } = header;
  const ch = channelsOfColorType(header.colorType);
  const rowBytes = Math.ceil((width * ch * bitDepth) / 8);
  const img = createImage(width, height);
  const ctx: Ctx = { header, palette, trns, key: colourKey(header.colorType, trns) };
  const s = new Array<number>(ch).fill(0);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      for (let c = 0; c < ch; c++) s[c] = sampleAt(rows, y * rowBytes, x * ch + c, bitDepth);
      writePixel(img.data, (y * width + x) * 4, s, ctx);
    }
  }
  return img;
}
